import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Users, MapPin, AlertTriangle, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AVTDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [districtCoverage, setDistrictCoverage] = useState([]);
  const [awcStats, setAwcStats] = useState(null);
  const [topBottom, setTopBottom] = useState(null);
  const [perfDistribution, setPerfDistribution] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedDistrict]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [overviewRes, coverageRes, awcRes, topBottomRes, perfRes, districtsRes] = await Promise.all([
        axios.get(`${API}/api/dashboards/avt/overview`),
        axios.get(`${API}/api/dashboards/avt/district-coverage?limit=50`),
        axios.get(`${API}/api/dashboards/avt/awc-visits${selectedDistrict !== 'all' ? `?district_id=${selectedDistrict}` : ''}`),
        axios.get(`${API}/api/dashboards/avt/top-bottom?metric=awc_visit_pct&limit=5`),
        axios.get(`${API}/api/dashboards/avt/performance-distribution`),
        axios.get(`${API}/api/dashboards/avt/districts`)
      ]);

      setOverview(overviewRes.data);
      setDistrictCoverage(coverageRes.data);
      setAwcStats(awcRes.data);
      setTopBottom(topBottomRes.data);
      setPerfDistribution(perfRes.data);
      setDistricts(districtsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceColor = (pct) => {
    if (pct >= 85) return '#22c55e';
    if (pct >= 70) return '#facc15';
    return '#ef4444';
  };

  const KPICard = ({ title, value, subtitle, icon: Icon, trend }) => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <h3 className="text-3xl font-bold mt-2">{value}</h3>
            {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
          </div>
          {Icon && (
            <div className={`p-3 rounded-full ${trend === 'up' ? 'bg-green-100' : trend === 'down' ? 'bg-red-100' : 'bg-blue-100'}`}>
              <Icon className={`w-6 h-6 ${trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-blue-600'}`} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 pt-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Field Monitoring & AVT Dashboard</h1>
          <p className="text-gray-600">Real-time monitoring of PO/CDPO/Supervisor visits and AWC coverage across Gujarat</p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-4">
          <Select value={selectedDistrict} onValueChange={setSelectedDistrict}>
            <SelectTrigger className="w-64 bg-white">
              <SelectValue placeholder="Select District" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Districts</SelectItem>
              {districts.map(district => (
                <SelectItem key={district} value={district}>{district}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KPICard
            title="Avg CDPO Visit Coverage"
            value={`${overview?.avg_cdpo_visit_pct}%`}
            subtitle={`${overview?.total_districts} districts`}
            icon={Users}
            trend={overview?.avg_cdpo_visit_pct >= 70 ? 'up' : 'down'}
          />
          <KPICard
            title="Avg Supervisor Coverage"
            value={`${overview?.avg_supervisor_visit_pct}%`}
            subtitle="Field supervision"
            icon={MapPin}
            trend={overview?.avg_supervisor_visit_pct >= 70 ? 'up' : 'down'}
          />
          <KPICard
            title="AWC Visit Coverage"
            value={`${overview?.avg_awc_visit_pct}%`}
            subtitle={`${overview?.awc_visited?.toLocaleString()} / ${overview?.total_awc?.toLocaleString()} AWCs`}
            icon={CheckCircle}
            trend={overview?.avg_awc_visit_pct >= 70 ? 'up' : 'down'}
          />
          <KPICard
            title="Districts Below Target"
            value={overview?.districts_below_threshold}
            subtitle="< 70% coverage"
            icon={AlertTriangle}
            trend="down"
          />
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="districts">District Analysis</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="data">Data Table</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* PO/CDPO/Supervisor Coverage Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Visit Coverage by Role</CardTitle>
                  <CardDescription>Top 10 districts - PO, CDPO, Supervisor visit percentages</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={districtCoverage.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="district" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="po_visit_pct" fill="#3b82f6" name="PO Visit %" />
                      <Bar dataKey="cdpo_visit_pct" fill="#10b981" name="CDPO Visit %" />
                      <Bar dataKey="supervisor_visit_pct" fill="#f59e0b" name="Supervisor Visit %" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Performance Distribution Pie */}
              <Card>
                <CardHeader>
                  <CardTitle>Performance Distribution</CardTitle>
                  <CardDescription>Districts categorized by AWC visit coverage</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={perfDistribution?.distribution || []}
                        dataKey="count"
                        nameKey="category"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={(entry) => `${entry.category}: ${entry.count}`}
                      >
                        {perfDistribution?.distribution?.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* AWC Visit Statistics */}
            <Card>
              <CardHeader>
                <CardTitle>AWC Visit Statistics</CardTitle>
                <CardDescription>Breakdown of AWC visits across {selectedDistrict === 'all' ? 'all districts' : selectedDistrict}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-600">Total AWCs</p>
                    <p className="text-2xl font-bold text-blue-700">{awcStats?.total_awc?.toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-gray-600">Visited</p>
                    <p className="text-2xl font-bold text-green-700">{awcStats?.visited?.toLocaleString()}</p>
                    <p className="text-sm text-green-600">{awcStats?.visit_pct}%</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <p className="text-sm text-gray-600">Not Visited</p>
                    <p className="text-2xl font-bold text-red-700">{awcStats?.not_visited?.toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <p className="text-sm text-gray-600">Not Opened</p>
                    <p className="text-2xl font-bold text-orange-700">{awcStats?.not_opened?.toLocaleString()}</p>
                    <p className="text-sm text-orange-600">{awcStats?.not_opened_pct}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* District Analysis Tab */}
          <TabsContent value="districts" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Performers */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    Top 5 Performers
                  </CardTitle>
                  <CardDescription>Districts with highest AWC visit coverage</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {topBottom?.top_performers?.map((district, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div>
                          <p className="font-medium">{district.district}</p>
                          <p className="text-sm text-gray-600">{district.total_awc} AWCs</p>
                        </div>
                        <div className="text-2xl font-bold text-green-700">{district.value}%</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Bottom Performers */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                    Bottom 5 Performers
                  </CardTitle>
                  <CardDescription>Districts needing immediate attention</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {topBottom?.bottom_performers?.map((district, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium">{district.district}</p>
                          <p className="text-sm text-gray-600">{district.total_awc} AWCs</p>
                        </div>
                        <div className="text-2xl font-bold text-red-700">{district.value}%</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance">
            <Card>
              <CardHeader>
                <CardTitle>AWC Visit Coverage by District</CardTitle>
                <CardDescription>All districts sorted by coverage percentage</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={600}>
                  <BarChart data={districtCoverage.sort((a, b) => b.awc_visit_pct - a.awc_visit_pct)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="district" type="category" width={150} />
                    <Tooltip />
                    <Bar dataKey="awc_visit_pct" name="AWC Visit %" radius={[0, 4, 4, 0]}>
                      {districtCoverage.map((entry, index) => (
                        <Cell key={index} fill={getPerformanceColor(entry.awc_visit_pct)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data Table Tab */}
          <TabsContent value="data">
            <Card>
              <CardHeader>
                <CardTitle>District-wise Detailed Data</CardTitle>
                <CardDescription>Complete metrics for all districts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">District</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total AWCs</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Visit %</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CDPO Visit %</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supervisor %</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">AWC Visit %</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {districtCoverage.map((district, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">{district.district}</td>
                          <td className="px-4 py-3">{district.total_awc}</td>
                          <td className="px-4 py-3">{district.po_visit_pct}%</td>
                          <td className="px-4 py-3">{district.cdpo_visit_pct}%</td>
                          <td className="px-4 py-3">{district.supervisor_visit_pct}%</td>
                          <td className="px-4 py-3">
                            <span className="font-bold" style={{ color: getPerformanceColor(district.awc_visit_pct) }}>
                              {district.awc_visit_pct}%
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              district.performance_rating === 'Excellent' ? 'bg-green-100 text-green-800' :
                              district.performance_rating === 'Good' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {district.performance_rating}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  </div>
  );
};

export default AVTDashboard;
